// SearchWarehouse.js
// Search warehouse - scanner and manual inputs for shelf, SKU, and product name.
// Uses searchSkuLocations and searchShelfItems to display results.

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ScannerSection, type ScannerSectionHandle } from '../scanner';
import { QrPlaceholderIcon } from '../scanner/WebcamPlaceholder';
import { performOCR } from '../../services/visionAPI';
import { findSKU, type SkuNameItem } from '../../services/SKUListAPI';
import {
  searchSkuLocations,
  searchShelfItems,
  getBrowseIndexes,
  getBrowseCheckedOutIndexes,
  getLabelByStorageIdPrefix,
} from '../../services/shelfIndexAPI';
import { extractStorageIdFromQr } from '../orders/orderdetail/scanningLogic';
import { QR_UNREADABLE_STORAGE_ID_MESSAGE } from '../scanner/qrScanMessages';
import {
  SCAN_LABEL_PREFIX_QUERY,
  parseStorageIdPrefixParam,
} from '../../utils/scanLabelHandoff';
import ProductInfoModal from '../shared/ProductInfoModal';
import { LabelCheckOutModal } from '../shared/StorageInfoModal';
import ShelfContentsModal from '../shared/ShelfContentsModal';
import ProductNameSearchModal from '../shared/ProductNameSearchModal';
import SkuAutocompleteInput from '../shared/SkuAutocompleteInput';
import { useSkuAutocomplete } from '../../hooks/useSkuAutocomplete';
import { useSkuNameCatalog } from '../../hooks/useSkuNameCatalog';
import { useSkuValidation } from '../../hooks/useSkuValidation';
import { useShelfValidation } from '../../hooks/useShelfValidation';
import { useHybridShelfIndexSync } from '../../hooks/useHybridShelfIndexSync';
import { useClickOutside } from '../../hooks/useClickOutside';
import {
  useStatusMessage,
  statusSlotMessageClass,
  cameraMessageClass,
  MESSAGE_TONES,
} from '../../hooks/useStatusMessage';
import { formatDateTimeShortParts } from '../../utils/formatCreatedAt';
import ShelfAutocompleteInput from '../shared/ShelfAutocompleteInput';
import OrderDetailCameraBar, { CameraIcon } from '../orders/orderdetail/OrderDetailCameraBar';
import { SHELF_RANGE, SKU_PREFIX } from '../../constants/app.constants';
import type {
  ShelfLocation,
  ShelfItem,
  BrowseIndexesEntry,
  BrowseCheckedOutIndexesEntry,
} from '../../types/apiTypes';
import { formatType, areLocationRowsEqual } from '../../utils/shelfIndexUtils';
import '../labels/PrintBoxLabel.css';
import '../orders/orderdetail/pick-list.css';
import './SearchWarehouse.css';

interface SelectedLabelState {
  loc: ShelfLocation;
  sku: string;
  onSuccess: () => Promise<void>;
  checkedIn: boolean;
}

function areShelfItemRowsEqual(a: ShelfItem[] = [], b: ShelfItem[] = []): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i];
    const right = b[i];
    if (
      left.id !== right.id ||
      left.sku !== right.sku ||
      left.type !== right.type ||
      left.shelf_index !== right.shelf_index
    ) {
      return false;
    }
  }
  return true;
}

function SearchWarehouse() {
  const [searchParams] = useSearchParams();
  const prefixHandoffAppliedRef = useRef(false);
  const [shelfIndex, setShelfIndex] = useState('');
  const [SKU, setSKU] = useState('');
  const [productName, setProductName] = useState('');
  const {
    message,
    tone: messageTone,
    invalidInput: errorInvalidInput,
    flashKey,
    fades: messageFades,
    showMessage,
  } = useStatusMessage();
  const [skuLocations, setSkuLocations] = useState<ShelfLocation[]>([]);
  const [skuLocationsLoading, setSkuLocationsLoading] = useState(false);
  const [shelfItems, setShelfItems] = useState<ShelfItem[]>([]);
  const [shelfItemsLoading, setShelfItemsLoading] = useState(false);
  const [showManualShelfInput, setShowManualShelfInput] = useState(false);
  const [shelfModalError, setShelfModalError] = useState<string | null>(null);
  const [showManualSKUInput, setShowManualSKUInput] = useState(false);
  const [manualSKU, setManualSKU] = useState(SKU_PREFIX);
  const [showManualNameInput, setShowManualNameInput] = useState(false);
  const [webcamOn, setWebcamOn] = useState(false);
  const [qrActive, setQrActive] = useState(false);
  const scannerCaptureRef = useRef<ScannerSectionHandle>(null);
  const skuInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const skipAutocompleteOpenRef = useRef(false);
  const skuResultsRef = useRef<HTMLDivElement>(null);
  const shelfResultsRef = useRef<HTMLDivElement>(null);

  const [browseExpanded, setBrowseExpanded] = useState(false);
  const [browseEntries, setBrowseEntries] = useState<BrowseIndexesEntry[]>([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseHasMore, setBrowseHasMore] = useState(false);
  const [browseOffset, setBrowseOffset] = useState(0);

  const [checkedOutExpanded, setCheckedOutExpanded] = useState(false);
  const [checkedOutEntries, setCheckedOutEntries] = useState<BrowseCheckedOutIndexesEntry[]>([]);
  const [checkedOutLoading, setCheckedOutLoading] = useState(false);
  const [checkedOutHasMore, setCheckedOutHasMore] = useState(false);
  const [checkedOutOffset, setCheckedOutOffset] = useState(0);

  const [shelfNumber, setShelfNumber] = useState('');
  const [shelfLetter, setShelfLetter] = useState('');
  const [shelfPosition, setShelfPosition] = useState('');
  const [manualShelfText, setManualShelfText] = useState('');
  const [productInfoItem, setProductInfoItem] = useState<{ sku: string } | null>(null);
  const [shelfContentsModalShelf, setShelfContentsModalShelf] = useState<string | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<SelectedLabelState | null>(null);

  const {
    allSkus: allSKUs,
    loading: skusLoading,
    error: skuLoadError,
    validateSku,
  } = useSkuValidation();
  const {
    loading: shelvesLoading,
    error: shelfLoadError,
    validateShelf,
    normalizeShelf,
  } = useShelfValidation();

  const {
    filteredSkus: filteredSKUs,
    showAutocomplete,
    setShowAutocomplete,
    selectedIndex,
    setSelectedIndex,
  } = useSkuAutocomplete({
    value: manualSKU,
    allSkus: allSKUs,
    prefix: SKU_PREFIX,
    skipAutocompleteOpenRef,
    inputRef: skuInputRef,
    autocompleteRef,
  });

  const { resolveNameForSku, rememberName } = useSkuNameCatalog();

  const productCriteriaActive = Boolean(SKU || productName);

  const applyProductSku = useCallback(
    (sku: string, name?: string): void => {
      setShelfIndex('');
      setSKU(sku);
      const resolved = name ?? resolveNameForSku(sku);
      setProductName(resolved);
      if (name) {
        rememberName(sku, name);
      }
    },
    [resolveNameForSku, rememberName],
  );

  const clearProductCriteria = useCallback((): void => {
    setSKU('');
    setProductName('');
  }, []);

  useEffect(() => {
    if (skuLoadError) {
      console.error('Error loading SKUs:', skuLoadError);
    }
  }, [skuLoadError]);

  useEffect(() => {
    if (shelfLoadError) {
      console.error('Error loading shelf names:', shelfLoadError);
    }
  }, [shelfLoadError]);

  const matchShelfIndex = (ocrText: string): string | null => {
    const shelfPattern = /\d{1,2}-[A-Za-z]-\d+/;
    const shelfMatch = ocrText.match(shelfPattern);
    return shelfMatch ? shelfMatch[0] : null;
  };

  const matchSKU = async (ocrText: string): Promise<string | null> => {
    const result = await findSKU(ocrText);
    return result?.SKUFound ? result.sku : null;
  };

  const cleanText = (ocrText: string): string => {
    const removeSpace = ocrText.replace(/\s+/g, '').toUpperCase();
    const regex = /^(\d+)-1-(\d+)$/;
    const match = removeSpace.match(regex);
    return match ? `${match[1]}-I-${match[2]}` : removeSpace;
  };

  const handleCapture = async (imageSrc: string | null): Promise<boolean> => {
    if (!imageSrc) return false;
    showMessage('Processing image...');
    const base64Image = imageSrc;
    const ocrText_raw = await performOCR(base64Image);

    if (ocrText_raw) {
      const ocrText = cleanText(ocrText_raw);
      const matchedSKU = await matchSKU(ocrText);
      const matchedShelfIndex = matchShelfIndex(ocrText);

      if (matchedSKU && matchedShelfIndex) {
        const skuValid = validateSku(matchedSKU);
        const normalizedShelf = normalizeShelf(matchedShelfIndex);
        const shelfValid = validateShelf(normalizedShelf);

        if (skuValid && shelfValid) {
          applyProductSku(matchedSKU);
          showMessage(`Matched SKU ${matchedSKU}.`, MESSAGE_TONES.SUCCESS);
          return true;
        }
        if (!skuValid) {
          showMessage(`SKU ${matchedSKU} Not found in Product DB`, MESSAGE_TONES.ERROR, {
            invalidInput: matchedSKU,
          });
          return false;
        }
        showMessage(`Shelf ${normalizedShelf} does not exist`, MESSAGE_TONES.ERROR, {
          invalidInput: normalizedShelf,
        });
        return false;
      }
      if (matchedShelfIndex) {
        const normalizedShelf = normalizeShelf(matchedShelfIndex);
        if (validateShelf(normalizedShelf)) {
          clearProductCriteria();
          setShelfIndex(normalizedShelf);
          showMessage(`Matched shelf index ${normalizedShelf}.`, MESSAGE_TONES.SUCCESS);
          return true;
        }
        showMessage(`Shelf ${normalizedShelf} does not exist`, MESSAGE_TONES.ERROR, {
          invalidInput: normalizedShelf,
        });
        return false;
      }
      if (matchedSKU) {
        if (validateSku(matchedSKU)) {
          applyProductSku(matchedSKU);
          showMessage(`Matched SKU ${matchedSKU}.`, MESSAGE_TONES.SUCCESS);
          return true;
        }
        showMessage(`SKU ${matchedSKU} Not found in Product DB`, MESSAGE_TONES.ERROR, {
          invalidInput: matchedSKU,
        });
        return false;
      }
      showMessage(`Could not match SKU or shelf. Scan input: ${ocrText}`, MESSAGE_TONES.ERROR, {
        invalidInput: ocrText,
      });
      return false;
    }
    showMessage('Error processing image. Please try again.', MESSAGE_TONES.ERROR);
    return false;
  };

  /** Bin label QR: resolve the 6-char storage ID to its SKU and search that SKU. */
  const handleQrCapture = useCallback(async (qrData: string | null): Promise<boolean> => {
    const prefix = extractStorageIdFromQr(qrData);
    if (!prefix) {
      const scanned = qrData?.trim();
      showMessage(QR_UNREADABLE_STORAGE_ID_MESSAGE, MESSAGE_TONES.ERROR, {
        invalidInput: scanned || null,
        fade: true,
      });
      return false;
    }
    showMessage('Looking up label...');
    const label = await getLabelByStorageIdPrefix(prefix);
    const sku = label?.sku?.trim();
    if (!sku) {
      showMessage(`Label not found in DB for storage ID ${prefix}`, MESSAGE_TONES.ERROR, {
        invalidInput: prefix,
      });
      return false;
    }
    if (!validateSku(sku)) {
      showMessage(`SKU ${sku} Not found in Product DB`, MESSAGE_TONES.ERROR, { invalidInput: sku });
      return false;
    }
    applyProductSku(sku);
    showMessage(`Matched ${prefix} — SKU ${sku}.`, MESSAGE_TONES.SUCCESS);
    return true;
  }, [applyProductSku, showMessage, validateSku]);

  useEffect(() => {
    if (prefixHandoffAppliedRef.current || skusLoading) return;
    const prefix = parseStorageIdPrefixParam(searchParams.get(SCAN_LABEL_PREFIX_QUERY));
    if (!prefix) return;
    prefixHandoffAppliedRef.current = true;
    void handleQrCapture(prefix);
  }, [handleQrCapture, searchParams, skusLoading]);

  const handleManualShelfInput = () => {
    let normalizedIndex: string;

    if (manualShelfText.trim()) {
      normalizedIndex = manualShelfText.trim().toUpperCase();
    } else if (shelfNumber && shelfLetter && shelfPosition) {
      normalizedIndex = `${shelfNumber}-${shelfLetter}-${shelfPosition}`.toUpperCase();
    } else {
      showMessage('Please select all three fields or enter a shelf name.', MESSAGE_TONES.ERROR, {
        autoClearMs: 2000,
      });
      return;
    }

    if (!validateShelf(normalizedIndex)) {
      setShelfModalError(`Shelf ${normalizedIndex} does not exist`);
      showMessage(`Shelf ${normalizedIndex} does not exist`, MESSAGE_TONES.ERROR, {
        invalidInput: normalizedIndex,
        autoClearMs: 5000,
      });
      return;
    }

    setShelfModalError(null);
    clearProductCriteria();
    setShelfIndex(normalizedIndex);
    setShowManualShelfInput(false);
    setShelfNumber('');
    setShelfLetter('');
    setShelfPosition('');
    setManualShelfText('');
    showMessage(`Shelf index ${normalizedIndex} set successfully.`, MESSAGE_TONES.SUCCESS, {
      autoClearMs: 2000,
    });
  };

  const handleManualSKUInputClose = () => {
    setShowManualSKUInput(false);
    setManualSKU(SKU_PREFIX);
  };

  const handleManualShelfInputClose = () => {
    setShowManualShelfInput(false);
    setShelfModalError(null);
    setShelfNumber('');
    setShelfLetter('');
    setShelfPosition('');
    setManualShelfText('');
  };

  const handleManualSKUInput = () => {
    if (manualSKU.trim() && manualSKU.trim().length > SKU_PREFIX.length) {
      const skuValue = manualSKU.trim().toUpperCase();

      if (!validateSku(skuValue)) {
        showMessage(`SKU ${skuValue} Not found in Product DB`, MESSAGE_TONES.ERROR, {
          invalidInput: skuValue,
          autoClearMs: 5000,
        });
        return;
      }

      applyProductSku(skuValue);
      handleManualSKUInputClose();
      setShowAutocomplete(false);
      showMessage(`SKU ${skuValue} set successfully.`, MESSAGE_TONES.SUCCESS, { autoClearMs: 2000 });
    } else {
      showMessage('Please enter a valid SKU.', MESSAGE_TONES.ERROR, { autoClearMs: 2000 });
    }
  };

  const handleSKUInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    if (value.length < SKU_PREFIX.length) {
      setManualSKU(SKU_PREFIX);
    } else if (!value.startsWith(SKU_PREFIX)) {
      setManualSKU(SKU_PREFIX + value.substring(SKU_PREFIX.length));
    } else {
      setManualSKU(value.toUpperCase());
    }
  };

  const handleSKUInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!showAutocomplete || filteredSKUs.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < filteredSKUs.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      selectSKU(filteredSKUs[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
    }
  };

  const selectSKU = (sku: string): void => {
    if (!validateSku(sku)) {
      showMessage(`SKU ${sku} Not found in Product DB`, MESSAGE_TONES.ERROR, {
        invalidInput: sku,
        autoClearMs: 5000,
      });
      return;
    }
    skipAutocompleteOpenRef.current = true;
    applyProductSku(sku);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
    handleManualSKUInputClose();
    showMessage(`SKU ${sku} set successfully.`, MESSAGE_TONES.SUCCESS, { autoClearMs: 2000 });
  };

  const selectNameResult = (item: SkuNameItem): void => {
    applyProductSku(item.sku, item.name);
    setShowManualNameInput(false);
    showMessage(`Name set for ${item.sku}.`, MESSAGE_TONES.SUCCESS, { autoClearMs: 2000 });
  };

  const refreshSkuLocations = useCallback(async (quiet = false, source = 'unknown') => {
    if (!SKU) {
      setSkuLocations([]);
      setSkuLocationsLoading(false);
      return;
    }
    const showLoading = !quiet;
    if (showLoading) {
      setSkuLocationsLoading(true);
    }
    // No blanket clear here: this runs right after a scan/manual set, and the
    // success copy for that action has to survive the auto-query.
    try {
      const result = await searchSkuLocations(SKU);
      if (result.error) {
        if (!quiet) {
          showMessage(result.error, MESSAGE_TONES.ERROR);
        }
      } else {
        const nextLocations = result.locations || [];
        setSkuLocations((prev) => (areLocationRowsEqual(prev, nextLocations) ? prev : nextLocations));
        if (!quiet && !result.locations?.length) {
          showMessage('No locations found for this SKU.', MESSAGE_TONES.WARNING);
        }
      }
    } catch {
      if (!quiet) {
        showMessage('Error searching SKU. Please try again.', MESSAGE_TONES.ERROR);
      }
    } finally {
      if (showLoading) {
        setSkuLocationsLoading(false);
      }
    }
  }, [SKU, showMessage]);

  useEffect(() => {
    refreshSkuLocations(false, 'sku-change');
  }, [refreshSkuLocations]);

  const refreshShelfItems = useCallback(async (quiet = false, source = 'unknown') => {
    if (!shelfIndex) {
      setShelfItems([]);
      setShelfItemsLoading(false);
      return;
    }
    const showLoading = !quiet;
    if (showLoading) {
      setShelfItemsLoading(true);
    }
    // See refreshSkuLocations: the triggering action's success copy must survive.
    try {
      const result = await searchShelfItems(shelfIndex);
      if (result.error) {
        if (!quiet) {
          showMessage(result.error, MESSAGE_TONES.ERROR);
        }
      } else {
        const nextItems = result.items || [];
        setShelfItems((prev) => (areShelfItemRowsEqual(prev, nextItems) ? prev : nextItems));
        if (!quiet && !result.items?.length) {
          showMessage('No items found on this shelf.');
        }
      }
    } catch {
      if (!quiet) {
        showMessage('Error loading shelf items. Please try again.', MESSAGE_TONES.ERROR);
      }
    } finally {
      if (showLoading) {
        setShelfItemsLoading(false);
      }
    }
  }, [shelfIndex, showMessage]);

  useEffect(() => {
    refreshShelfItems(false, 'shelf-change');
  }, [refreshShelfItems]);

  useEffect(() => {
    if (SKU && !skuLocationsLoading && skuResultsRef.current) {
      skuResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [SKU, skuLocationsLoading]);

  useEffect(() => {
    if (shelfIndex && !shelfItemsLoading && shelfResultsRef.current) {
      shelfResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [shelfIndex, shelfItemsLoading]);

  useClickOutside(
    [autocompleteRef, skuInputRef],
    () => setShowAutocomplete(false),
  );

  // When the name catalog loads after a SKU was already selected, fill Name.
  useEffect(() => {
    if (!SKU || productName) return;
    const name = resolveNameForSku(SKU);
    if (name) {
      setProductName(name);
    }
  }, [SKU, productName, resolveNameForSku]);

  const refreshBrowse = useCallback(async () => {
    if (!browseExpanded) return;
    setBrowseLoading(true);
    try {
      const { entries, hasMore } = await getBrowseIndexes(0, 20);
      setBrowseEntries(entries);
      setBrowseHasMore(hasMore);
      setBrowseOffset(entries.length);
    } catch {
      setBrowseEntries([]);
      setBrowseHasMore(false);
    } finally {
      setBrowseLoading(false);
    }
  }, [browseExpanded]);

  useEffect(() => {
    if (!browseExpanded) return;
    const fetchBrowse = async (offset = 0, append = false) => {
      setBrowseLoading(true);
      try {
        const { entries, hasMore } = await getBrowseIndexes(offset, 20);
        setBrowseEntries((prev) => (append ? [...prev, ...entries] : entries));
        setBrowseHasMore(hasMore);
        setBrowseOffset(offset + entries.length);
      } catch (err) {
        console.error('Error loading browse indexes:', err);
        setBrowseEntries((prev) => (append ? prev : []));
        setBrowseHasMore(false);
      } finally {
        setBrowseLoading(false);
      }
    };
    fetchBrowse(0, false);
  }, [browseExpanded]);

  const handleHybridSync = useCallback(async (source = 'unknown') => {
    const syncTasks = [];
    if (SKU) {
      syncTasks.push(refreshSkuLocations(true, `hybrid-${source}`));
    }
    if (shelfIndex) {
      syncTasks.push(refreshShelfItems(true, `hybrid-${source}`));
    }
    if (syncTasks.length > 0) {
      await Promise.all(syncTasks);
    }
  }, [SKU, shelfIndex, refreshSkuLocations, refreshShelfItems]);

  useHybridShelfIndexSync({
    enabled: true,
    onSync: handleHybridSync,
    pollMs: 20000,
  });

  const handleShowMoreBrowse = async () => {
    setBrowseLoading(true);
    try {
      const { entries, hasMore } = await getBrowseIndexes(browseOffset, 20);
      setBrowseEntries((prev) =>
         [...prev, ...entries]);
      setBrowseHasMore(hasMore);
      setBrowseOffset((prev) => prev + entries.length);
    } catch (err) {
      console.error('Error loading more browse indexes:', err);
      setBrowseHasMore(false);
    } finally {
      setBrowseLoading(false);
    }
  };

  const refreshCheckedOut = useCallback(async () => {
    if (!checkedOutExpanded) return;
    setCheckedOutLoading(true);
    try {
      const { entries, hasMore } = await getBrowseCheckedOutIndexes(0, 20);
      setCheckedOutEntries(entries);
      setCheckedOutHasMore(hasMore);
      setCheckedOutOffset(entries.length);
    } catch {
      setCheckedOutEntries([]);
      setCheckedOutHasMore(false);
    } finally {
      setCheckedOutLoading(false);
    }
  }, [checkedOutExpanded]);

  useEffect(() => {
    if (!checkedOutExpanded) return;
    const fetchCheckedOut = async (offset = 0, append = false) => {
      setCheckedOutLoading(true);
      try {
        const { entries, hasMore } = await getBrowseCheckedOutIndexes(offset, 20);
        setCheckedOutEntries((prev) => (append ? [...prev, ...entries] : entries));
        setCheckedOutHasMore(hasMore);
        setCheckedOutOffset(offset + entries.length);
      } catch (err) {
        console.error('Error loading checked-out indexes:', err);
        setCheckedOutEntries((prev) => (append ? prev : []));
        setCheckedOutHasMore(false);
      } finally {
        setCheckedOutLoading(false);
      }
    };
    fetchCheckedOut(0, false);
  }, [checkedOutExpanded]);

  const handleShowMoreCheckedOut = async () => {
    setCheckedOutLoading(true);
    try {
      const { entries, hasMore } = await getBrowseCheckedOutIndexes(checkedOutOffset, 20);
      setCheckedOutEntries((prev) => [...prev, ...entries]);
      setCheckedOutHasMore(hasMore);
      setCheckedOutOffset((prev) => prev + entries.length);
    } catch (err) {
      console.error('Error loading more checked-out indexes:', err);
      setCheckedOutHasMore(false);
    } finally {
      setCheckedOutLoading(false);
    }
  };

  /** Date and time each get their own unbreakable line. */
  function renderCheckedOutAt(isoString: string | null): React.ReactNode {
    const parts = isoString ? formatDateTimeShortParts(isoString) : null;
    if (!parts) return '—';
    return (
      <>
        <span className="sw-checked-out-date">{parts.date}</span>
        <span className="sw-checked-out-time">{parts.time}</span>
      </>
    );
  }

  const numbers1to10 = SHELF_RANGE.NUMBERS_1_TO_10;
  const lettersAtoZ = SHELF_RANGE.LETTERS_A_TO_Z;
  const numbers1to6 = SHELF_RANGE.POSITIONS_1_TO_6;

  // Re-key on each message so a repeat re-runs its flash / fade animation.
  const messageKey = `${messageTone}-${flashKey}`;
  const messageBody = errorInvalidInput && message.includes(errorInvalidInput) ? (
    <>
      {message.split(errorInvalidInput)[0]}
      <span className="pbl-message-invalid">{errorInvalidInput}</span>
      {message.split(errorInvalidInput).slice(1).join(errorInvalidInput)}
    </>
  ) : (
    message
  );
  // Camera open: status rides on the preview so it is not hidden behind the sheet.
  const cameraMessageOverlay = message ? (
    <div key={messageKey} className={cameraMessageClass(messageTone, messageFades)} role="status">
      {messageBody}
    </div>
  ) : null;

  return (
    <div className={`pbl-container sw-page${webcamOn ? ' sw-camera-open' : ''}`}>
      {(skusLoading || shelvesLoading) && (
        <div className="pbl-loading">
          <div className="pbl-loading-spinner"></div>
          <div className="pbl-loading-text">Loading databases...</div>
        </div>
      )}

      {/* Reserved status slot between the navbar H1 and Search Criteria; keeps the
          page from shifting as copy appears. Camera open: see cameraMessageOverlay. */}
      <div className="pbl-page-header-status" role="status" aria-live="polite">
        {message && !webcamOn ? (
          <div key={messageKey} className={statusSlotMessageClass(messageTone)}>
            {messageBody}
          </div>
        ) : null}
      </div>

      <div className="pbl-section">
        <div className="pbl-section-title pbl-section-info">
          <span className="pbl-section-text">Search Criteria</span>
        </div>
        <div className="pbl-info-display">
          <div className={`pbl-info-row ${shelfIndex ? 'sw-disabled' : ''}`}>
            <span className="pbl-info-label">SKU:</span>
            <div className="pbl-info-value-wrapper">
              <span
                className={`pbl-info-value pbl-info-clickable ${!SKU ? 'pbl-info-empty' : ''}`}
                onClick={() => {
                  if (shelfIndex) return;
                  setShowManualSKUInput(true);
                  setShowManualShelfInput(false);
                  setShowManualNameInput(false);
                  setManualSKU(SKU || SKU_PREFIX);
                  setTimeout(() => {
                    const modal = document.querySelector('.pbl-modal-overlay');
                    if (modal) modal.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    if (skuInputRef.current) {
                      skuInputRef.current.focus();
                      const len = skuInputRef.current.value.length;
                      skuInputRef.current.setSelectionRange(len, len);
                    }
                  }, 100);
                }}
              >
                {SKU || 'Scan or enter manually'}
              </span>
              {SKU && (
                <button
                  className="pbl-info-clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearProductCriteria();
                    showMessage('SKU cleared', MESSAGE_TONES.WARNING, { autoClearMs: 2000 });
                  }}
                  title="Clear SKU"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <div className={`pbl-info-row ${shelfIndex ? 'sw-disabled' : ''}`}>
            <span className="pbl-info-label">Name:</span>
            <div className="pbl-info-value-wrapper">
              <span
                className={`pbl-info-value pbl-info-clickable ${!productName ? 'pbl-info-empty' : ''}`}
                onClick={() => {
                  if (shelfIndex) return;
                  setShowManualNameInput(true);
                  setShowManualSKUInput(false);
                  setShowManualShelfInput(false);
                  setTimeout(() => {
                    const modal = document.querySelector('.pbl-modal-overlay');
                    if (modal) modal.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
              >
                {productName || 'Search by product or variant name'}
              </span>
              {productName && (
                <button
                  className="pbl-info-clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearProductCriteria();
                    showMessage('Name cleared', MESSAGE_TONES.INFO, { autoClearMs: 2000 });
                  }}
                  title="Clear Name"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <div className={`pbl-info-row ${productCriteriaActive ? 'sw-disabled' : ''}`}>
            <span className="pbl-info-label">Shelf:</span>
            <div className="pbl-info-value-wrapper">
              <span
                className={`pbl-info-value pbl-info-clickable ${!shelfIndex ? 'pbl-info-empty' : ''}`}
                onClick={() => {
                  if (productCriteriaActive) return;
                  setShowManualShelfInput(true);
                  setShowManualSKUInput(false);
                  setShowManualNameInput(false);
                  if (shelfIndex) {
                    const parts = shelfIndex.split('-');
                    if (parts.length === 3) {
                      setShelfNumber(parts[0]);
                      setShelfLetter(parts[1]);
                      setShelfPosition(parts[2]);
                    }
                  }
                }}
              >
                {shelfIndex || 'Scan or enter manually'}
              </span>
              {shelfIndex && (
                <button
                  className="pbl-info-clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShelfIndex('');
                    showMessage('Shelf cleared', MESSAGE_TONES.INFO, { autoClearMs: 2000 });
                  }}
                  title="Clear Shelf"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        {SKU && (
          <div ref={skuResultsRef} className="sw-results-section">
            <div className="sw-results-title">SKU Locations</div>
            {skuLocationsLoading ? (
              <div className="sw-results-empty">Loading...</div>
            ) : skuLocations.length === 0 ? (
              <div className="sw-results-empty">No locations found</div>
            ) : (
              <div className="sw-table-scroll">
              <table className="sw-results-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>SKU</th>
                    <th>Type</th>
                    <th>Shelf</th>
                  </tr>
                </thead>
                <tbody>
                  {skuLocations.map((loc, idx) => (
                    <tr key={`${loc.shelf_index}-${loc.id}-${idx}`}>
                      <td className="sw-results-id-cell">
                        <button
                          type="button"
                          className="sw-results-id-clickable"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLabel({
                              loc: { id: loc.id, shelf_index: loc.shelf_index, type: loc.type },
                              sku: SKU,
                              onSuccess: refreshSkuLocations,
                              checkedIn: false
                            });
                          }}
                        >
                          {loc.id}
                        </button>
                      </td>
                      <td
                        className="sw-sku-clickable"
                        onClick={() => setProductInfoItem({ sku: SKU })}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setProductInfoItem({ sku: SKU });
                          }
                        }}
                      >
                        {SKU}
                      </td>
                      <td className="sw-results-type-cell">{formatType(loc.type)}</td>
                      <td
                        className="sw-shelf-clickable"
                        onClick={() => setShelfContentsModalShelf(loc.shelf_index)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setShelfContentsModalShelf(loc.shelf_index);
                          }
                        }}
                      >
                        {loc.shelf_index}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}

        {shelfIndex && (
          <div ref={shelfResultsRef} className="sw-results-section">
            <div className="sw-results-title">Items on Shelf</div>
            {shelfItemsLoading ? (
              <div className="sw-results-empty">Loading...</div>
            ) : shelfItems.length === 0 ? (
              <div className="sw-results-empty">No items found</div>
            ) : (
              <div className="sw-table-scroll">
              <table className="sw-results-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>SKU</th>
                    <th>Type</th>
                    <th>Shelf</th>
                  </tr>
                </thead>
                <tbody>
                  {shelfItems.map((item, idx) => (
                    <tr key={`${item.sku}-${item.id}-${idx}`}>
                      <td className="sw-results-id-cell">
                        <button
                          type="button"
                          className="sw-results-id-clickable"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLabel({
                              loc: { id: item.id, shelf_index: item.shelf_index, type: item.type },
                              sku: item.sku,
                              onSuccess: refreshShelfItems,
                              checkedIn: false
                            });
                          }}
                        >
                          {item.id}
                        </button>
                      </td>
                      <td
                        className="sw-sku-clickable"
                        onClick={() => setProductInfoItem({ sku: item.sku })}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setProductInfoItem({ sku: item.sku });
                          }
                        }}
                      >
                        {item.sku}
                      </td>
                      <td className="sw-results-type-cell">{formatType(item.type)}</td>
                      <td className="sw-results-index-cell">{item.shelf_index}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="sw-browse-section">
        <div
          className="sw-browse-title"
          onClick={() => setBrowseExpanded((prev) => !prev)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setBrowseExpanded((prev) => !prev);
            }
          }}
        >
          <span className="pbl-section-text">Browse Indexes</span>
          <span className={`sw-browse-triangle ${browseExpanded ? 'expanded' : ''}`}>▶</span>
        </div>
        {browseExpanded && (
          <div className="sw-browse-content">
            {browseLoading && browseEntries.length === 0 ? (
              <div className="sw-browse-empty">Loading...</div>
            ) : browseEntries.length === 0 ? (
              <div className="sw-browse-empty">No entries found</div>
            ) : (
              <>
                <div className="sw-table-scroll">
                <table className="sw-browse-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>SKU</th>
                      <th>Shelf Index</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {browseEntries.map((entry, idx) => (
                      <tr key={`${entry.id}-${idx}`}>
                        <td className="sw-results-id-cell">
                          <button
                            type="button"
                            className="sw-results-id-clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLabel({
                                loc: { id: entry.id, shelf_index: entry.shelf_index, type: entry.type },
                                sku: entry.sku,
                                onSuccess: refreshBrowse,
                                checkedIn: false
                              });
                            }}
                          >
                            {entry.id}
                          </button>
                        </td>
                        <td
                          className="sw-browse-sku sw-sku-clickable"
                          onClick={() => setProductInfoItem({ sku: entry.sku })}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setProductInfoItem({ sku: entry.sku });
                            }
                          }}
                        >
                          {entry.sku}
                        </td>
                        <td className="sw-browse-shelf">{entry.shelf_index}</td>
                        <td className="sw-results-type-cell">{formatType(entry.type)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {browseHasMore && (
                  <button
                    type="button"
                    className="sw-browse-more"
                    onClick={handleShowMoreBrowse}
                    disabled={browseLoading}
                  >
                    {browseLoading ? 'Loading...' : 'Show more entries'}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="sw-browse-section">
        <div
          className="sw-browse-title"
          onClick={() => setCheckedOutExpanded((prev) => !prev)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setCheckedOutExpanded((prev) => !prev);
            }
          }}
        >
          <span className="pbl-section-text">See Checked Out ID&apos;s</span>
          <span className={`sw-browse-triangle ${checkedOutExpanded ? 'expanded' : ''}`}>▶</span>
        </div>
        {checkedOutExpanded && (
          <div className="sw-browse-content">
            {checkedOutLoading && checkedOutEntries.length === 0 ? (
              <div className="sw-browse-empty">Loading...</div>
            ) : checkedOutEntries.length === 0 ? (
              <div className="sw-browse-empty">No checked-out entries found</div>
            ) : (
              <>
                <div className="sw-table-scroll">
                <table className="sw-browse-table sw-browse-table-5col">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>SKU</th>
                      <th>Shelf Index</th>
                      <th>Type</th>
                      <th>Checked Out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkedOutEntries.map((entry, idx) => (
                      <tr key={`${entry.id}-${idx}`}>
                        <td className="sw-results-id-cell">
                          <button
                            type="button"
                            className="sw-results-id-clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLabel({
                                loc: { id: entry.id, shelf_index: entry.shelf_index, type: entry.type },
                                sku: entry.sku,
                                onSuccess: refreshCheckedOut,
                                checkedIn: true
                              });
                            }}
                          >
                            {entry.id}
                          </button>
                        </td>
                        <td
                          className="sw-browse-sku sw-sku-clickable"
                          onClick={() => setProductInfoItem({ sku: entry.sku })}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setProductInfoItem({ sku: entry.sku });
                            }
                          }}
                        >
                          {entry.sku}
                        </td>
                        <td className="sw-browse-shelf">{entry.shelf_index}</td>
                        <td className="sw-results-type-cell">{formatType(entry.type)}</td>
                        <td className="sw-checked-out-cell">{renderCheckedOutAt(entry.checked_out_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {checkedOutHasMore && (
                  <button
                    type="button"
                    className="sw-browse-more"
                    onClick={handleShowMoreCheckedOut}
                    disabled={checkedOutLoading}
                  >
                    {checkedOutLoading ? 'Loading...' : 'Show more entries'}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {showManualShelfInput && (
        <div className="pbl-modal-overlay" onClick={handleManualShelfInputClose}>
          <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pbl-modal-close" onClick={handleManualShelfInputClose}>×</button>
            <div className="pbl-modal-title">Enter Shelf Index</div>
            <div className="pbl-manual-popup">
              <div className="pbl-dropdown-container">
                <select value={shelfNumber} onChange={(e) => { setShelfNumber(e.target.value); setManualShelfText(''); }} className="pbl-dropdown">
                  <option value="">--</option>
                  {numbers1to10.map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                <span className="pbl-separator">-</span>
                <select value={shelfLetter} onChange={(e) => { setShelfLetter(e.target.value); setManualShelfText(''); }} className="pbl-dropdown">
                  <option value="">--</option>
                  {lettersAtoZ.map(letter => (
                    <option key={letter} value={letter}>{letter}</option>
                  ))}
                </select>
                <span className="pbl-separator">-</span>
                <select value={shelfPosition} onChange={(e) => { setShelfPosition(e.target.value); setManualShelfText(''); }} className="pbl-dropdown">
                  <option value="">--</option>
                  {numbers1to6.map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              <div className="pbl-or-divider">or</div>
              <ShelfAutocompleteInput
                value={manualShelfText}
                onChange={(val) => { setManualShelfText(val); setShelfNumber(''); setShelfLetter(''); setShelfPosition(''); }}
                onSelect={(shelf) => { setManualShelfText(shelf); setShelfNumber(''); setShelfLetter(''); setShelfPosition(''); }}
                placeholder="Type shelf name"
              />
              {shelfModalError && (
                <div className="pbl-message" style={{ color: '#ff6b6b' }}>{shelfModalError}</div>
              )}
              <button
                onClick={handleManualShelfInput}
                className="pbl-submit-button"
                disabled={(!shelfNumber || !shelfLetter || !shelfPosition) && !manualShelfText.trim()}
              >
                Set Shelf Index
              </button>
            </div>
          </div>
        </div>
      )}

      {showManualSKUInput && (
        <div className="pbl-modal-overlay" onClick={handleManualSKUInputClose}>
          <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pbl-modal-close" onClick={handleManualSKUInputClose}>×</button>
            <div className="pbl-modal-title">Enter SKU</div>
            <div className="pbl-manual-popup">
              <SkuAutocompleteInput
                inputRef={skuInputRef}
                autocompleteRef={autocompleteRef}
                value={manualSKU}
                onChange={handleSKUInputChange}
                onKeyDown={handleSKUInputKeyDown}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && selectedIndex < 0) handleManualSKUInput();
                }}
                placeholder={`${SKU_PREFIX}...`}
                showAutocomplete={showAutocomplete}
                filteredSkus={filteredSKUs}
                selectedIndex={selectedIndex}
                onSelectSku={selectSKU}
                onHoverIndex={setSelectedIndex}
              />
              <button
                onClick={handleManualSKUInput}
                className="pbl-submit-button"
                disabled={!manualSKU.trim() || manualSKU.trim().length <= SKU_PREFIX.length}
              >
                Set SKU
              </button>
            </div>
          </div>
        </div>
      )}

      <ProductNameSearchModal
        open={showManualNameInput}
        initialQuery={productName}
        onClose={() => setShowManualNameInput(false)}
        onSelect={selectNameResult}
      />

      {shelfContentsModalShelf && (
        <ShelfContentsModal
          shelf={shelfContentsModalShelf}
          onClose={() => setShelfContentsModalShelf(null)}
        />
      )}

      {productInfoItem && (
        <ProductInfoModal
          item={productInfoItem}
          onClose={() => setProductInfoItem(null)}
        />
      )}

      {selectedLabel && (
        <LabelCheckOutModal
          loc={selectedLabel.loc}
          sku={selectedLabel.sku}
          onClose={() => setSelectedLabel(null)}
          onSuccess={selectedLabel.onSuccess}
          checkedIn={selectedLabel.checkedIn ?? false}
        />
      )}

      <OrderDetailCameraBar
        open={webcamOn}
        onShutter={() => scannerCaptureRef.current?.capture()}
        leftActions={
          <div className="order-detail-bottom-bar-left-group">
            <button
              type="button"
              className="order-detail-scan-mode-toggle"
              onClick={() => setQrActive((prev) => !prev)}
              aria-pressed={qrActive}
              title={qrActive ? 'Switch to text (OCR) scan' : 'Switch to QR code scan'}
              aria-label={qrActive ? 'Scan mode: QR. Switch to text.' : 'Scan mode: text. Switch to QR.'}
            >
              <span className={`order-detail-scan-mode-option${qrActive ? ' active' : ''}`}>
                <QrPlaceholderIcon />
              </span>
              <span className={`order-detail-scan-mode-option${!qrActive ? ' active' : ''}`}>
                <span className="placeholder-text-chip">a..z</span>
              </span>
            </button>
          </div>
        }
        centerActions={
          <button
            type="button"
            className="order-detail-bar-camera-btn order-detail-bar-camera-btn-center"
            onClick={() => setWebcamOn(true)}
            aria-label="Open camera"
          >
            <CameraIcon size={28} />
          </button>
        }
      >
        <ScannerSection
          ref={scannerCaptureRef}
          mode={qrActive ? 'qr' : 'barcode'}
          onCapture={qrActive ? handleQrCapture : handleCapture}
          webcamOn={webcamOn}
          setWebcamOn={setWebcamOn}
          className="webcam-capture-container"
          overlay={cameraMessageOverlay}
          cornerAction={
            <button
              type="button"
              className="webcam-viewport-close-btn"
              onClick={(event) => {
                event.stopPropagation();
                setWebcamOn(false);
              }}
              aria-label="Close camera"
            >
              ×
            </button>
          }
        />
      </OrderDetailCameraBar>
    </div>
  );
}

export default SearchWarehouse;
